import React, { useState } from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { setChatMessageFeedbackService } from "../../utils/chatService";

const ChatMessageFeedbackModal = ({ messageId, chatId, onClose, onSuccess }) => {
  const options = ["Inappropriate message", "Irrelevant content", "Abusive language"]; 
  const [selected, setSelected] = useState("");

  const handleSelect = async (option) => {
    try {
      setSelected(option);

      const result = await setChatMessageFeedbackService({
        messageId,
        chatId,
        feedbackType: "Bad",
        reason: option,
      });

      if (result.status === 200) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      console.error("Error submitting bad feedback", err);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose(); 
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
      onClick={handleOverlayClick}
    >
      <div className="bg-white rounded-xl shadow-lg p-6 w-96">
        <h2 className="text-lg font-semibold mb-4">Why did you dislike this?</h2>

        <div className="space-y-2">
          {options.map((option) => (
            <button
              key={option}
              onClick={() => handleSelect(option)}
              className={`w-full text-left px-3 py-2 rounded-md border transition
                ${selected === option 
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-800 hover:bg-gray-300 hover:text-gray-900" 
                }`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const ChatMessageFeedbackButtons = ({ messageId, chatId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [hovered, setHovered] = useState(null); // "good" | "bad" | null

  const handleGood = async () => {
    try {
      const result = await setChatMessageFeedbackService({
        messageId,
        chatId,
        feedbackType: "Good",
        reason: "Good",
      });

      if (result.status === 200) {
        setIsDisabled(true); 
      }
    } catch (err) {
      console.error("Error submitting good feedback", err);
    }
  };

  const handleBad = () => {
    setIsModalOpen(true);
  };

  if (!messageId || !chatId) {
    console.warn('Message ID or chat ID is not set');
    return null;
  }

  return (
    <div className="flex space-x-4 my-4 items-center">
      <div className="flex items-center relative space-x-4">
        <button
          onClick={handleGood}
          disabled={isDisabled}
          className={`ml-0 p-0 rounded-full hover:bg-gray-100 bg-transparent hover:bg-transparent ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
          onMouseEnter={() => setHovered("good")}
          onMouseLeave={() => setHovered(null)}
        >
          <ThumbsUp size={20} className="text-stone-500 hover:text-stone-800" /> 
        </button>
        {hovered === "good" && (
          <div
            className="flex justify-center items-center absolute left-1/2 -translate-x-1/2 shadow mt-2 w-max bg-slate-600 text-white text-xs font-semibold px-2 py-1 rounded"
            style={{ top: "100%", zIndex: "200" }}
          >
            Good Response
          </div>
        )}
      </div>

      <div className="flex space-x-4 items-center relative">
        <button
          onClick={handleBad}
          disabled={isDisabled}
          className={`ml-0 p-0 rounded-full hover:bg-gray-100 bg-transparent hover:bg-transparent ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
          onMouseEnter={() => setHovered("bad")}
          onMouseLeave={() => setHovered(null)}
        >
          <ThumbsDown size={20} className="text-stone-500 hover:text-stone-800" /> 
        </button>
        {hovered === "bad" && (
          <div
            className="flex justify-center items-center absolute left-1/2 -translate-x-1/2 mt-2 w-max bg-slate-600 text-white text-xs font-semibold px-2 py-1 rounded shadow z-50"
            style={{ top: "100%", zIndex: "200" }}
          >
            Bad Response
          </div>
        )}
      </div>

      {isModalOpen && (
        <ChatMessageFeedbackModal
          messageId={messageId}
          chatId={chatId}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => setIsDisabled(true)}
        />
      )}
    </div>
  );
};

export default ChatMessageFeedbackButtons;
